// Parsec Cloud (https://parsec.cloud) Copyright (c) AGPL-3.0-only 2016-present Scille SAS

import { registerPlugin } from '@capacitor/core';

import { LibParsecPlugin } from '@/plugins/libparsec/definitions';
import { LoadWebLibParsecPlugin } from '@libparsec_trampoline';

const _libparsec = registerPlugin<LibParsecPlugin>('LibParsec', {
  electron: () => (window as any).libparsec_plugin,

  // In Web we have the whole libparsec compiled in Wasm and provided as a module
  // that we can import just like regular js.
  // This is cool but has an unexpected side effect: this module is going to be
  // packaged even if we are not building the application for web.
  // Worst: if we are building from a clean repo, the Wasm module doesn't exist
  // at all and the packaging fails :'(
  //
  // To avoid this we have to make sure the code that contains the import of the
  // Wasm module is only present when building the application for web.
  // We do this by extracting the `LoadWebLibParsecPlugin` into a `trampoline.ts`, this
  // file having two implementation (the real one when building for the web and a dummy
  // one otherwise) that are selected when `vue-cli-service` is called (see `vue.config.js`).
  web: LoadWebLibParsecPlugin,

  // In Android, capacitor already knows about the plugin given it has been registered from Java
});

function logError(level: 'warn' | 'error', message: string): void {
  // may not be available right at the start
  if (window.nativeAPI && window.nativeAPI.log) {
    window.nativeAPI.log(level, message);
  } else {
    console[level](message);
  }
}

interface SkipData {
  tag: string;
  function: string;
}

const FUNCTIONS_TO_SKIP: Array<SkipData> = [
  {
    tag: 'WorkspaceStatEntryErrorEntryNotFound',
    function: 'workspaceStatEntry',
  },
  {
    tag: 'ParseParsecAddrErrorInvalidUrl',
    function: 'parseParsecAddr',
  },
  {
    tag: 'WorkspaceCreateFolderErrorEntryExists',
    function: 'workspaceCreateFolderAll',
  },
  {
    tag: 'ClientListFrozenUsersErrorAuthorNotAllowed',
    function: 'clientListFrozenUsers',
  },
];

const FUNCTIONS_TO_SLOW_DOWN: Array<string> = [
  'workspaceStatEntry',
  'clientListWorkspaces',
  'workspaceInfo',
  'clientListUsers',
  'workspaceStatFolderChildrenById',
  'clientOrganizationInfo',
  'bootstrapOrganization',
  'clientInfo',
  'clientListInvitations',
  'clientTotpSetupStatus',
];

const MOCKS: Array<{ name: string; funct: (..._args: Array<any>) => Promise<any> }> = [];

class ParsecProxy {
  get(target: LibParsecPlugin, name: any) {
    return async (...args: any[]): Promise<any> => {
      try {
        if ((window as any).TESTING) {
          const mock = MOCKS.find((mock) => mock.name === name);
          if (mock) {
            console.log(`Using mocked '${name}'`);
            return await mock.funct(...args);
          }
        }
        if ((window as any).TESTING_ENABLE_SNAIL_MODE === true && FUNCTIONS_TO_SLOW_DOWN.includes(name)) {
          console.log(`Slowing down '${name}'`);
          await new Promise<void>((resolve) => {
            setTimeout(() => {
              resolve();
            }, Math.random() * 1500);
          });
        }
        // @ts-expect-error Dont know how to fix the `...arg` properly so that the linter doesn't complain
        const result = await target[name as keyof LibParsecPlugin](...args);
        if (
          result &&
          (result as any).ok === false &&
          !FUNCTIONS_TO_SKIP.find((sd) => sd.function === name && (result as any).error && (result as any).error.tag === sd.tag)
        ) {
          const resultError = result as { ok: boolean; error: { tag: string; error: string } };
          logError('warn', `Error when calling ${name}: ${JSON.stringify(resultError.error)}`);
        }
        return result;
      } catch (err: any) {
        logError('error', `Exception when calling ${name}: ${String(err)}`);
        return { ok: false, error: { tag: 'bindings-exception', error: String(err) } };
      }
    };
  }
  set(_target: LibParsecPlugin, name: any, value: any) {
    if (!(window as any).TESTING) {
      return false;
    }
    console.log(`Replacing '${name}'`);
    MOCKS.push({ name: name, funct: value });
    return false;
  }
}

const proxy = new Proxy(_libparsec, new ParsecProxy());

export const libparsec = proxy;

export * from '@/plugins/libparsec/definitions';

// Global exposition of libparsec for easier debugging with console
declare global {
  interface Window {
    libparsec: LibParsecPlugin;
  }
}
window.libparsec = libparsec;

if ((window as any).TESTING) {
  (window as any).clearLibParsecMocks = () => {
    MOCKS.splice(0, MOCKS.length);
  };
}
